import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { BoundingBox } from '../BoundingBox.js';
import { AdditionalInfo, Exam } from '../exam.js';
import { Point } from '../Point.js';
import { createBoundingBoxWithOCR } from './agents/extraction-modules/ocr-utils.js';
import { OcrExtractionType } from './agents/extraction-modules/ocrExtractionTypes.js';
import { TaskType } from './task-types/task-main.js';

interface AdditionalInfoSaveItem {
  id: string;
  boundingBoxId: string;
  imageId: string | null;
  approved: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number; // 0-indexed on client
  ocrExtractedText: string;
}

export const saveAdditionalInfoMutation = registerMutation({
  operationName: 'saveAdditionalInfoMutation',
  type: () => Boolean,
  params: [
    { name: 'examId', type: () => String },
    { name: 'imageIds', type: () => [String] },
    { name: 'additionalInfoJson', type: () => String },
  ],
  resolve:
    (examId: string, imageIds: string[] = [], additionalInfoJson = '[]') =>
    async () => {
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      if (!exam) {
        logger.warn({ examId, context: 'saveAdditionalInfoMutation' }, 'Exam not found');
        return true;
      }

      let items: AdditionalInfoSaveItem[] = [];
      try {
        items = JSON.parse(additionalInfoJson);
      } catch {
        logger.warn({ context: 'saveAdditionalInfoMutation' }, 'Invalid JSON for additionalInfoJson');
        return true;
      }

      if (!Array.isArray(items)) return true;

      // Build a lookup map from the current DB state
      const dbMap = new Map<string, any>();
      if (Array.isArray((exam as any).additionalInformation)) {
        for (const entry of (exam as any).additionalInformation) {
          const entryId = String(entry?._id ?? '');
          if (entryId) dbMap.set(entryId, entry);
        }
      }

      const result: AdditionalInfo[] = [];

      for (const item of items) {
        if (!item.id || !item.boundingBoxId) continue;

        const needsOCR = !item.imageId;

        if (!needsOCR && dbMap.has(item.id)) {
          // Keep DB entry verbatim — no re-OCR, no data loss
          result.push(new AdditionalInfo(dbMap.get(item.id)));
          continue;
        }

        // New item or box was moved (imageId cleared) → run OCR
        const bbox = {
          x1: item.x * 10,
          y1: item.y * 10,
          x2: (item.x + item.width) * 10,
          y2: (item.y + item.height) * 10,
        };
        const pageNumber = item.pageNumber + 1; // convert to 1-indexed

        let boundingBox: BoundingBox;
        try {
          boundingBox = await createBoundingBoxWithOCR(
            { bbox, pageNumber, boundingBoxId: item.boundingBoxId },
            imageIds,
            OcrExtractionType.ADDITIONAL_INFO,
            TaskType.DEFAULT,
          );
        } catch (error) {
          logger.error({ err: error, itemId: item.id, context: 'saveAdditionalInfoMutation' }, 'OCR failed for item, using fallback');
          boundingBox = new BoundingBox({
            _id: item.boundingBoxId,
            pageNumber,
            topLeft: new Point({ x: bbox.x1, y: bbox.y1 }),
            bottomRight: new Point({ x: bbox.x2, y: bbox.y2 }),
            imageId: null,
            ocrExtractedText: '',
            confidence: 0,
            isHandwritten: false,
          });
        }

        result.push(
          new AdditionalInfo({
            _id: item.id,
            content: boundingBox.ocrExtractedText || '',
            boundingBoxes: [boundingBox],
          }),
        );
      }

      // Items present in DB but not in input are omitted → they were deleted locally
      const updateSet: any = { additionalInformation: result.map(a => a.toObject()) };
      await examCollection.updateOne({ _id: examId }, { $set: updateSet });

      logger.info({ count: result.length, examId, context: 'saveAdditionalInfoMutation' }, 'Saved additionalInfo items for exam');
      return true;
    },
});
